import { Box, Grid, Tab, Tabs } from "@mui/material";
import { Button } from "@platform/design-system";
import { useCallback, useEffect, useState } from "react";

import { RFPS_SUBMITTED } from "../constants";
import { UseRfpsPageDataReturn } from "../hooks/useMainRfpsPageDataV2";
import { RFP_STATUSES } from "../constants/rfp";

import { RfpStatusFilter } from "./RfpStatusFilter";
import { DraftRFPsTable } from "./DraftRfpsTable";
import { RFPsTable } from "./RfpsTable";

import { SearchInput } from ".";

type DualRfpsTablesProps = {
  isBrokerPartner?: boolean;
  data: UseRfpsPageDataReturn;
};

export const DualRfpsTables: React.FC<DualRfpsTablesProps> = ({
  isBrokerPartner,
  data,
}) => {
  const {
    tabIndex,
    submittedCount,
    draftCount,
    debouncedQuery,
    deepLinkedQuery,
    debouncedSearch,
    currStatus,
    setCurrStatus,
    dataTableOptions,
    handleTabChange,
    handleDiscardRfpDraft,
  } = data;
  const { loading } = dataTableOptions;

  const [search, setSearch] = useState<string>(debouncedQuery);

  // Follow ?q= from the header search's "See all RFPs" when it changes while
  // this page is already mounted. Only the URL drives this, never the
  // debounced query, so text a user is typing is left alone.
  useEffect(() => {
    if (deepLinkedQuery != null) setSearch(deepLinkedQuery);
  }, [deepLinkedQuery]);

  const handleSearch = useCallback(
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      const term = target.value;

      setSearch(term);
      debouncedSearch(term);
    },
    [debouncedSearch, setSearch],
  );

  const handleSearchKeyPress = useCallback(
    (event: React.KeyboardEvent): void => {
      if (event.code === "Enter" && search.trim().length > 0) {
        // Trigger immediate search on Enter
        debouncedSearch(search.trim());
      }
    },
    [search, debouncedSearch],
  );

  const handleClearFilters = useCallback(() => {
    setSearch("");
    debouncedSearch("");
    setCurrStatus([]);
  }, [debouncedSearch, setCurrStatus, setSearch]);

  return (
    <Grid container mb={5} data-testid="dual-rfps-tables">
      {!isBrokerPartner && (
        <Grid size={12} sx={{ borderBottom: 1, borderColor: "divider" }} mt={3}>
          <Tabs
            className="detail-page-tabs"
            value={tabIndex}
            onChange={(_: React.SyntheticEvent<Element, Event>, value) => {
              handleTabChange(value);
              setSearch("");
            }}
            aria-label="client detail tabs"
          >
            <Tab label={`SUBMITTED (${submittedCount})`} />
            <Tab label={`DRAFTS (${draftCount})`} />
          </Tabs>
        </Grid>
      )}
      <Grid container size={12} mt={5}>
        <Box mb={2} sx={{ width: "100%" }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 7 }}>
              <SearchInput
                onChange={handleSearch}
                onKeyDown={handleSearchKeyPress}
                fullWidth
                value={search}
              />
            </Grid>
            {/* Only show status filters on submitted tab */}
            {tabIndex === RFPS_SUBMITTED && (
              <Grid
                size={{ xs: 12, md: 5 }}
                display="flex"
                alignItems="center"
                gap={2}
              >
                <RfpStatusFilter
                  currStatus={currStatus}
                  setCurrStatus={setCurrStatus}
                  selectProps={{ disabled: loading, multiple: true }}
                  options={RFP_STATUSES}
                />
                <Button
                  variant="tertiary"
                  disabled={!search && currStatus.length === 0}
                  onClick={handleClearFilters}
                >
                  Clear All
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>

        {tabIndex === RFPS_SUBMITTED ? (
          <RFPsTable
            isBrokerPartner={isBrokerPartner}
            options={dataTableOptions}
          />
        ) : (
          <DraftRFPsTable
            isBrokerPartner={isBrokerPartner}
            options={dataTableOptions}
            onDiscardRfpDraft={handleDiscardRfpDraft}
          />
        )}
      </Grid>
    </Grid>
  );
};
